import { useCallback, useEffect, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Map } from 'immutable';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import {
  fetchEmployeeTabData,
  updateUserInfo as getUpdateUserInfo,
} from 'actions/employeeView';
import { fetchRosterData as getFetchRosterData } from 'actions/teamView';

import {
  getEmployeeViewUserFromProps,
  getIsEmployeeTabLoading,
} from 'selectors/employeeView';
import {
  getCurrentUserId,
  getIsPayrollEnrolled,
  getPayrollHandleUserMergeRequestPhase2Enabled,
  getPayrollUSStateOptions,
  getStateOptions,
} from 'selectors/session';

import Box from 'components/Box';
import Button from 'components/clickables/Button';
import Icon from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator';
import Text from 'components/Text';

import { cxHelpers } from 'util/className';
const { cx } = cxHelpers('AddPayrollInformationForm');

import { usePersonalInformationEditState } from 'hooks/usePersonalInformationEditState';

import SsnValidationErrorBanner from 'features/employeeProfile/components/SsnValidationErrorBanner';
import BankingInformationWidget from 'features/employeeProfile/PersonalInformationView/BankingInformationWidget';
import EmployeeInformationWidget from 'features/employeeProfile/PersonalInformationView/EmployeeInformationWidget';
import TaxInformationWidget from 'features/employeeProfile/PersonalInformationView/TaxInformationWidget';

import RadioGroup from 'components/forms/RadioGroup';

const PAPER_CHECK_PAYMENT_OPTION = 'paper_check';
const DIRECT_DEPOSIT_PAYMENT_OPTION = 'direct_deposit';

const PAYMENT_OPTIONS = [
  {
    value: PAPER_CHECK_PAYMENT_OPTION,
    description: 'Paper Check',
  },
  {
    value: DIRECT_DEPOSIT_PAYMENT_OPTION,
    description: 'Direct Deposit',
  },
];

const TAB_NAME = 'personal_information';

export const AddPayrollInformationForm = ({
  userId,
  onClickBackButton,
  updateUserInfo,
  stateOptions,
  payrollUSStateOptions,
  fetchTabData,
  fetchRosterData,
  isPayrollEnrolled,
  user,
  isLoading,
  mobileTablet,
  onClickDone,
  invalidPayrollInfo,
  payrollHandleUserMergeRequestPhase2Enabled,
  currentUserId,
}) => {
  const [sectionEditMode, setSectionEditMode] = useState({
    personalInfo: true,
    bankingInfo: !user?.get('decrypted_account_number'),
  });

  const [ssnValidationError, setSsnValidationError] = useState(false);

  const handleOnClickEdit = useCallback(
    (section, editMode) => {
      setSectionEditMode({ ...sectionEditMode, [section]: editMode });
    },
    [sectionEditMode]
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    user?.get('decrypted_account_number')
      ? DIRECT_DEPOSIT_PAYMENT_OPTION
      : PAPER_CHECK_PAYMENT_OPTION
  );
  const { first_name: firstName } = user?.toJS() || {};

  const {
    disableEdits,
    showEmployeeWillBeNotifiedAlert,
    employeeInformationEditButton,
    showDrawerEmployeeInformationAlert,
    showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert,
  } = usePersonalInformationEditState({
    isRolloutEnabled: payrollHandleUserMergeRequestPhase2Enabled,
    user,
    currentUserId,
  });

  useEffect(() => {
    if (userId) {
      fetchRosterData();
      // needs to force reload because the userId is the same between back and forth navigation
      // so redux needs a way to know to reload the data. Without force, if some
      // change occurs, PayrollInfo view won't show the correct fields.
      // Eg: show ssn for contractor_type individual or ein for business
      fetchTabData(userId, TAB_NAME, { force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnClickBackButton = useCallback(() => {
    onClickBackButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBankingInfoEditButton = useMemo(
    () =>
      !sectionEditMode.bankingInfo &&
      selectedPaymentMethod === DIRECT_DEPOSIT_PAYMENT_OPTION &&
      (!user.get('decrypted_account_number') ||
        !user.get('decrypted_routing_number')),
    [sectionEditMode, user, selectedPaymentMethod]
  );

  const handleOnChangePaymentMethod = useCallback(option => {
    setSelectedPaymentMethod(option);
  }, []);

  const showForm = useMemo(() => {
    if (userId) {
      return !isLoading && user;
    }

    return true;
  }, [isLoading, user, userId]);

  const handleOnSsnValidationError = useCallback(setSsnValidationError, []);

  const stateOptionsToUse = isPayrollEnrolled
    ? payrollUSStateOptions
    : stateOptions;

  return (
    <Box>
      <Box className={cx()} p={32}>
        <Box row vcenter>
          <Icon
            type="chevronLeftLight"
            size="md"
            color="blue"
            onClick={handleOnClickBackButton}
          />
          <Box ml12>
            <Text fs24 fw600 i18n="edit_team.individual_drawer.title" />
          </Box>
        </Box>
        {userId && isLoading && (
          <Box hcenter mt32>
            <LoadingIndicator />
          </Box>
        )}
        {showForm && (
          <>
            {invalidPayrollInfo(user) && (
              <Box p={12} bg="alertA1" bradius={8} mt24>
                <Text
                  fs14
                  fw400
                  navy
                  i18n="edit_team.individual_drawer.banner_info"
                  i18nProps={{ first_name: user.get('first_name') }}
                />
              </Box>
            )}
            {ssnValidationError && (
              <SsnValidationErrorBanner user={user} type="drawer" />
            )}
            <Box bb mt24>
              <Box mb24 spacebetween>
                <Text
                  fs={20}
                  fw600
                  navy
                  i18n="edit_team.individual_drawer.section_title.personal_information"
                />
                {!sectionEditMode.personalInfo && (
                  <Button
                    data-testid="personal-info-edit"
                    theme="link"
                    icon="edit"
                    iconSize={20}
                    onClick={partial(handleOnClickEdit, 'personalInfo', true)}
                  />
                )}
              </Box>
              <EmployeeInformationWidget
                user={user || Map({})}
                teamDrawerMode
                mobile
                updateUserInfo={updateUserInfo}
                stateOptions={stateOptionsToUse}
                initEditMode={sectionEditMode.personalInfo}
                onSaveCompleted={partial(
                  handleOnClickEdit,
                  'personalInfo',
                  false
                )}
                onSsnValidationError={handleOnSsnValidationError}
                disableEdits={disableEdits}
                showEmployeeWillBeNotifiedAlert={
                  showDrawerEmployeeInformationAlert
                }
                employeeInformationEditButton={employeeInformationEditButton}
                showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert={
                  showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert
                }
              />
            </Box>
            <Box bb mt24 pb={12}>
              <Box mb24 spacebetween>
                <Text
                  fs={20}
                  fw600
                  navy
                  i18n="edit_team.individual_drawer.section_title.payment_method"
                />
                {showBankingInfoEditButton && (
                  <Button
                    data-testid="banking-info-edit"
                    theme="link"
                    icon="edit"
                    iconSize={20}
                    onClick={partial(handleOnClickEdit, 'bankingInfo', true)}
                  />
                )}
              </Box>
              <Box>
                <RadioGroup
                  data-testid="payment-option"
                  value={selectedPaymentMethod}
                  options={PAYMENT_OPTIONS}
                  radioStyle={{ paddingLeft: 0 }}
                  onChange={handleOnChangePaymentMethod}
                />
              </Box>
              {selectedPaymentMethod === DIRECT_DEPOSIT_PAYMENT_OPTION && (
                <Box mt24>
                  {(showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert ||
                    showEmployeeWillBeNotifiedAlert) && (
                    <Box p={12} bg="blueLightest" bradius={12} mb24>
                      <Text
                        i18n="team.employee_profile.payroll_ee_information.banner_content"
                        i18nProps={{
                          employee_first_name: firstName,
                        }}
                      />
                    </Box>
                  )}

                  <BankingInformationWidget
                    user={user}
                    teamDrawerMode
                    mobile
                    initEditMode={sectionEditMode.bankingInfo}
                    updateUserInfo={updateUserInfo}
                    onSaveCompleted={partial(
                      handleOnClickEdit,
                      'bankingInfo',
                      false
                    )}
                  />
                </Box>
              )}
            </Box>
            <Box mt24>
              <TaxInformationWidget
                user={user}
                teamDrawerMode
                updateUserInfo={updateUserInfo}
              />
            </Box>
          </>
        )}
      </Box>
      <Box hright bt p={32}>
        <Box w="50%">
          <Button
            fullWidth
            size={mobileTablet ? 'small' : 'medium'}
            theme="secondary-blue"
            onClick={onClickDone}
          >
            <Text
              fw600
              fs={mobileTablet ? 16 : 18}
              i18n="edit_team.individual_drawer.done"
            />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

AddPayrollInformationForm.propTypes = {
  userId: PropTypes.number,
  user: ImmutablePropTypes.map.isRequired,
  updateUserInfo: PropTypes.func.isRequired,
  mobile: PropTypes.bool,
  stateOptions: ImmutablePropTypes.list.isRequired,
  isLoading: PropTypes.bool,
  fetchRosterData: PropTypes.func.isRequired,
  fetchTabData: PropTypes.func.isRequired,
  onClickDone: PropTypes.func.isRequired,
  invalidPayrollInfo: PropTypes.func,
};

export default connect(
  (state, props) => ({
    isPayrollEnrolled: getIsPayrollEnrolled(state, props),
    stateOptions: getStateOptions(state, props),
    payrollUSStateOptions: getPayrollUSStateOptions(state),
    isLoading: getIsEmployeeTabLoading(state, {
      id: props.userId,
      tab: TAB_NAME,
    }),
    user: getEmployeeViewUserFromProps(state, props),
    payrollHandleUserMergeRequestPhase2Enabled:
      getPayrollHandleUserMergeRequestPhase2Enabled(state),
    currentUserId: getCurrentUserId(state),
  }),
  {
    fetchRosterData: getFetchRosterData,
    fetchTabData: fetchEmployeeTabData,
    updateUserInfo: getUpdateUserInfo,
  }
)(AddPayrollInformationForm);
